"use client";
import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAppDispatch } from "lib/redux/hooks";
import { setAccessToken, setIsPremiumUser, setSubscriptionCreditCount } from "lib/redux/uiSlice";
import { googleOneTapSignIn } from "api/googleOneTapApi";
import { getUserProfile } from "api/userApi";
import { getSubscriptionInfo } from "api/subscription/subscriptionInfo";
import { initializeGAUserTracking } from "lib/hooks/useGAUserTracking";
import { toast } from "react-toastify";

const DEFAULT_REDIRECT = "/dashboard";

// Same validation logic as OAuth redirect handler
const getCurrentPage = (currentPage: string | null) => {
  if (!currentPage) return DEFAULT_REDIRECT;

  const validPages = [
    "/",
    "/resume-builder",
    "/premium-subscription",
    "/resume-writing",
    "/upload-resume",
    "/referral",
    "/affiliate",
    "/select-experience",
    "/extension/connect",
    "/score-check",
    "/score-check/overall",
    "/score-check/personal",
    "/score-check/experience",
    "/score-check/education",
    "/score-check/skills",
    "/score-check/projects",
  ];

  // Extract base path for validation (handles URLs with query params)
  const basePath = currentPage.split('?')[0];
  
  if (validPages.includes(basePath)) {
    // For extension/connect, preserve full URL with query params
    if (basePath === '/extension/connect') {
      return currentPage; // Return full URL with nonce and extId
    }
    return basePath === "/" ? DEFAULT_REDIRECT : basePath;
  }

  return DEFAULT_REDIRECT;
};

export default function OneTapTopRight() {
	const router = useRouter();
	const dispatch = useAppDispatch();
	const pathname = usePathname();

	useEffect(() => {
		// Only run on client side
		if (typeof window === "undefined") return;
		
		// Don't show if user is already logged in
		if (localStorage.getItem("access-token")) {
			// console.log("[One Tap] User already logged in, skipping");
			return;
		}

		// Pages where One Tap should NOT be shown (to avoid interrupting specific flows)
		const excludedPages = [
			"/extension/connect",  // Extension authentication flow
			"/referral",           // Referral flow
			"/affiliate",          // Affiliate flow
		];

		// Check if current page should be excluded
		if (excludedPages.includes(pathname)) {
			// console.log("[One Tap] Skipping on excluded page:", pathname);
			return;
		}

		localStorage.setItem("current-page", pathname);
		// console.log("[One Tap] Saved current page:", pathname);

		// Wait for Google Identity Services to load
		const waitForGsi = async () => {
			for (let i = 0; i < 50; i++) {
				// @ts-ignore
				if (window.google?.accounts?.id) return true;
				await new Promise((r) => setTimeout(r, 100));
			}
			return false;
		};

		// Handle One Tap credential callback
		
		const handleCredentialResponse = async (response: any) => {
			const credential = response?.credential;
			if (!credential) {
				// console.error("[One Tap] No credential received");
				return;
			}

			try {
				
				const { accessToken } = await googleOneTapSignIn(credential);

				if (!accessToken) {
					console.error("[One Tap] No access token received from backend");
					return;
				}

				localStorage.setItem("access-token", accessToken);
				dispatch(setAccessToken(accessToken));

				// Fetch user profile for GA tracking
				try {
					const userProfile = await getUserProfile();
					if (userProfile && userProfile.id) {
						initializeGAUserTracking(userProfile.id, dispatch);
					}
				} catch (profileError) {
					// Continue even if profile fetch fails
				}

				// Fetch subscription info to update premium status and credits
				try {
					const subscriptionInfo = await getSubscriptionInfo();
					dispatch(setSubscriptionCreditCount(subscriptionInfo.credits));
					dispatch(setIsPremiumUser(subscriptionInfo.premium));
				} catch (subscriptionError) {
					console.error("[One Tap] Failed to fetch subscription info:", subscriptionError);
					// Continue even if subscription fetch fails
				}

				// Use the same redirect logic as OAuth for consistency
				const currentPage = localStorage.getItem("current-page");
				const redirectPage = getCurrentPage(currentPage);
				setTimeout(() => {
					router.push(redirectPage);
				}, 100);

			} catch (error: any) {
				// console.error("[One Tap] Authentication failed:", error);
				toast.error("Server Error. Please try again.");
				// Show user-friendly error message
				if (error.response?.status === 401) {
					console.error("[One Tap] Invalid credential - verification failed");
				} else {
					// console.error("[One Tap] Network or server error");
				}
			}
		};

		// Initialize Google One Tap
		const initializeOneTap = async () => {
			const gsiLoaded = await waitForGsi();
			
			if (!gsiLoaded) {
				// console.warn("[One Tap] Google Identity Services not loaded");
				return;
			}

			// @ts-ignore
			const gsi = window.google.accounts.id;
			
			// Initialize with callback
			gsi.initialize({
				client_id: "31384632577-8baskiv1ckophcdnuhaq3d0q35vr7bvp.apps.googleusercontent.com",
				callback: handleCredentialResponse,
				auto_select: false, // Don't auto-select - let user choose
				cancel_on_tap_outside: true, // Dismiss if user clicks outside
			});

			// Show the One Tap prompt
			gsi.prompt((notification: any) => {
				// Handle prompt notifications
				if (notification.isNotDisplayed()) {
					// console.log("[One Tap] Prompt not displayed:", notification.getNotDisplayedReason());
				} else if (notification.isSkippedMoment()) {
					// console.log("[One Tap] Prompt skipped:", notification.getSkippedReason());
				} else if (notification.isDismissedMoment()) {
					// console.log("[One Tap] Prompt dismissed:", notification.getDismissedReason());
				}
			});
		};

		initializeOneTap();
	}, [router, dispatch, pathname]);

	return null;
}
